import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Avatar, 
  Typography,
  Grid, 
  Skeleton, 
  Alert,
  Button,
  Modal,
  TextField,
  InputLabel,
  Input,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { FaUser, FaEnvelope, FaPhone, FaShieldAlt, FaCloudUploadAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { postFileUpload, postData } from '../../Webservice/webservice';

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    avatar: null,
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData = sessionStorage.getItem('user');
        
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setFormData({
            firstName: parsedUser.firstName || parsedUser.name || parsedUser.fullName || '',
            lastName: parsedUser.lastName || parsedUser.lName || '',
            email: parsedUser.email || '',
            phoneNumber: parsedUser.phoneNumber || parsedUser.phone || parsedUser.contact || '',
            avatar: parsedUser.avatar || parsedUser.profileImage || parsedUser.image || null,
          });
          setImagePreview(parsedUser.avatar || parsedUser.profileImage || parsedUser.image || null);
        } else {
          setError('No user data found. Please log in again.');
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Error loading user data. Please log in again.');
      } finally {
        setLoading(false);
      }
    };
    loadUserData();
  }, []);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setModalError(null);
    setImagePreview(user.avatar || user.profileImage || user.image || null);
  };

  const handleOpenPasswordModal = () => setOpenPasswordModal(true);
  const handleClosePasswordModal = () => {
    setOpenPasswordModal(false);
    setPasswordError(null);
    setPasswordData({ oldPassword: '', newPassword: '' });
    setShowOldPassword(false);
    setShowNewPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'email' && modalError?.includes('email')) {
      setModalError(null);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordError) {
      setPasswordError(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, avatar: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError(null);

    if (!formData.firstName || !formData.lastName || !formData.email) {
      setModalError('Please fill in all required fields (First Name, Last Name, Email).');
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      const dataToSend = new FormData();
      if (formData.firstName) dataToSend.append('firstName', formData.firstName);
      if (formData.lastName) dataToSend.append('lastName', formData.lastName);
      if (formData.email) dataToSend.append('email', formData.email);
      if (formData.phoneNumber) dataToSend.append('phoneNumber', formData.phoneNumber);
      if (formData.avatar instanceof File) dataToSend.append('image', formData.avatar);

      const response = await postFileUpload('profile/update', dataToSend, token);
      if (response) {
        const updatedUser = {
          ...user,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          avatar: formData.avatar instanceof File ? URL.createObjectURL(formData.avatar) : formData.avatar,
        };
        setUser(updatedUser);
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        handleCloseModal();
      } else {
        setModalError(response.message || 'Failed to update user data.');
      }
    } catch (err) {
      console.error('Error updating user data:', err);
      if (err.message.includes('E11000 duplicate key error')) {
        setModalError('This email is already in use. Please choose a different email.');
      } else {
        setModalError('Error updating user data. Please try again.');
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (!passwordData.oldPassword || !passwordData.newPassword) {
      setPasswordError('Please fill in both old and new password fields.');
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      const response = await postData('profile/update/password', passwordData, token);
      if (response) {
        handleClosePasswordModal();
      } else {
        setPasswordError(response.message || 'Failed to update password.');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError('Error updating password. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6 }}>
        <Box
          sx={{
            maxWidth: 900,
            mx: 'auto',
            display: 'flex',
            gap: 4,
            background: '#fff',
            borderRadius: 3,
            boxShadow: 2,
            p: 4,
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#f7fafd',
              borderRadius: 3,
              p: 4,
              minWidth: 260
            }}
          >
            <Skeleton variant="circular" width={180} height={180} />
            <Skeleton variant="text" width={160} height={28} sx={{ mt: 2 }} />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
          <Box sx={{
            flex: 2,
            pl: { md: 6, xs: 0 },
            pt: { xs: 4, md: 0 },
            background: '#f7fafd',
            borderRadius: 3,
          }}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <Box key={idx} sx={{ mb: 1.5 }}>
                <Skeleton variant="rectangular" width="100%" height={40} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 6 }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
          No user data available. Please log in again.
        </Alert>
      </Box>
    );
  }

  const userData = {
    firstName: user.firstName || user.name || user.fullName || 'User',
    lastName: user.lastName || user.lName || 'User',
    email: user.email || 'No email provided',
    role: user.role || user.userType || 'User',
    country: user.country || user.location || 'Not specified',
    avatar: user.avatar || user.profileImage || user.image || 'no image',
    emailVerification: user.emailVerification || user.emailVerified || 'Pending',
    phoneNumber: user.phoneNumber || user.phone || user.contact || 'Not provided',
    status: user.status || (user.isActive ? 'Inactive' : 'Active')
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h5" component="h1" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: "#1a1a1a", display: 'flex', alignItems: 'center', gap: 1 }}>
          User Profile
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleOpenModal}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              textTransform: 'none',
              backgroundColor: '#2B333A',
              color: 'white',
            //   border: '1px solid black',
              '&:hover': { backgroundColor: '#2B333A' }
            }}
          >
            Edit Profile
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenPasswordModal}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              textTransform: 'none',
              backgroundColor: '#2B333A',
              color: 'white',
            //   border: '1px solid black',
              '&:hover': { backgroundColor: '#2B333A' }
            }}
          >
            Update Password
          </Button>
        </Box>
      </Box>
     <Box
  sx={{
    maxWidth: 1200,
    mx: 'auto',
    display: 'flex',
    gap: { xs: 3, md: 6 },
    background: '#fff',
    borderRadius: 3,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
    p: { xs: 3, md: 5 },
    flexDirection: { xs: 'column', md: 'row' },
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      // background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    }
  }}
>
  {/* Left Profile Section */}
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: '#f4f5f7',
      borderRadius: 3,
      p: { xs: 3, md: 4 },
      minWidth: { xs: '100%', md: 280 },
      border: '1px solid rgba(99, 102, 241, 0.1)',
    }}
  >
    <Avatar
      src={userData.avatar}
      alt={userData.firstName}
      sx={{
        width: { xs: 140, md: 160 },
        height: { xs: 140, md: 160 },
        mb: 3,
        border: '4px solid white',
      }}
    />
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{
        mt: 1,
        fontFamily: 'Inter, sans-serif',
        background: 'black',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textAlign: 'center'
      }}
    >
      {userData.firstName} {userData.lastName}
    </Typography>
    <Typography
      color="text.secondary"
      sx={{
        fontSize: 14,
        mt: 1,
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        textAlign: 'center'
      }}
    >
      <FaEnvelope style={{ fontSize: 12 }} />
      {userData.email}
    </Typography>
    
    {/* Status Badge */}
    <Box
      sx={{
        mt: 3,
        px: 3,
        py: 1,
        borderRadius: 20,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: userData.status === 'Active' ? '#22c55e' : '#f97316',
          animation: 'pulse 2s infinite'
        }}
      />
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          color: userData.status === 'Active' ? '#22c55e' : '#f97316',
          fontFamily: 'Inter, sans-serif'
        }}
      >
        {userData.status}
      </Typography>
    </Box>
  </Box>

  {/* Right Info Section */}
  <Box
    sx={{
      flex: 2,
      display: 'flex',
      flexDirection: 'column',
      background: '#f4f5f7',
      borderRadius: 3,
      p: { xs: 3, md: 4 },
      border: '1px solid rgba(99, 102, 241, 0.1)',
    }}
  >
    <Typography
      variant="h6"
      fontWeight={700}
      sx={{
        mb: 3,
        fontFamily: 'Inter, sans-serif',
        color: '#1e293b',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      {/* <FaInfoCircle /> */}
      Personal Information
    </Typography>

    <Grid container spacing={3}>
      {[
        { icon: <FaUser />, label: "First Name", value: userData.firstName },
        { icon: <FaUser />, label: "Last Name", value: userData.lastName },
        { icon: <FaShieldAlt />, label: "Role", value: userData.role },
        { icon: <FaEnvelope />, label: "Email", value: userData.email },
        { icon: <FaPhone />, label: "Contact", value: userData.phoneNumber },
      ].map((item, index) => (
        <Grid item xs={12} sm={6} key={index}>
          <Box
            sx={{
              borderRadius: 2,
              p: 2.5,
              height: '100%',
            
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1
              }}
            >
              {item.icon}
              {item.label}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#1e293b',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 15
              }}
            >
              {item.value}
            </Typography>
          </Box>
        </Grid>
      ))}
      
      {/* Role with special styling */}
      <Grid item xs={12}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(99, 146, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
            borderRadius: 2,
            p: 3,
            border: '1px solid rgba(99, 102, 241, 0.2)',
            mt: 1
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#2B333A',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1
            }}
          >
            <FaShieldAlt />
            Account Role
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#2B333A',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 18
            }}
          >
            {userData.role}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Box>
</Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="edit-profile-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="edit-profile-modal" variant="h6" component="h2" sx={{ mb: 3, fontFamily: 'Mulish, sans-serif' }}>
            Edit Profile
          </Typography>
          {modalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {modalError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={imagePreview}
                alt="Profile Preview"
                sx={{ width: 100, height: 100, boxShadow: 1 }}
              />
            </Box>
            <InputLabel htmlFor="avatar-upload" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
              <FaCloudUploadAlt />
              Upload Profile Image
            </InputLabel>
            <Input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              sx={{ display: 'none' }}
            />
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              fullWidth
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  color: 'red',
                  borderColor: 'red',
                  '&:hover': { borderColor: 'red' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  backgroundColor: '#2B333A',
                  color: 'white',
                //   border: '1px solid black',
                  '&:hover': { backgroundColor: '#2B333A' }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openPasswordModal}
        onClose={handleClosePasswordModal}
        aria-labelledby="update-password-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="update-password-modal" variant="h6" component="h2" sx={{ mb: 3, fontFamily: 'Mulish, sans-serif' }}>
            Update Password
          </Typography>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Old Password"
              name="oldPassword"
              type={showOldPassword ? 'text' : 'password'}
              value={passwordData.oldPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <FaEye /> :  <FaEyeSlash /> }
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New Password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <FaEye /> :  <FaEyeSlash />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClosePasswordModal}
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  color: 'red',
                  borderColor: 'red',
                  '&:hover': { borderColor: 'red' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  backgroundColor: '#2B333A',
                  color: 'white',
                //   border: '1px solid black',
                  '&:hover': { backgroundColor: '#2B333A' }
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

function InfoRow({ icon, label, value, valueColor = '#222', fontWeight = 600 }) {
  return (
    <Box
      sx={{
        background: '#f6fafd',
        borderRadius: 2,
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        mb: 1
      }}
    >
      <Box sx={{ mr: 2, color: '#888' }}>
        {icon}
      </Box>
      <Typography sx={{ flex: 1, color: '#888', fontWeight: 500, fontSize: 15 }}>
        {label}
      </Typography>
      <Typography sx={{ mx: 1, color: '#888', fontWeight: 500, fontSize: 15 }}>
        :
      </Typography>
      <Typography
        sx={{
          flex: 2,
          color: valueColor,
          fontWeight,
          fontSize: 15,
          wordBreak: 'break-all'
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default Profile;